import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useCallback, useMemo } from 'react';

import {
  type CanaryCompanyVisibilityScore,
  useCanaryVisibilityScoreByCompany,
} from '@/api/aiVisibility';
import { DataTable, type DataTableColumnDef } from '@/components/DataTable';
import DeltaLabel from '@/components/kpi/DeltaLabel';
import LetteredAvatar from '@/components/LetteredAvatar';
import { Panel } from '@/components/panels';
import { numberFormat } from '@/helper/numberFormatter';

const AIVisibilityCompetitorTable = () => {
  const { scores, isLoading, error } = useCanaryVisibilityScoreByCompany();
  const companies = useMemo(
    () => [...(scores?.customer ? [scores.customer] : []), ...(scores?.competitors ?? [])],
    [scores],
  );
  const customerId = scores?.customer?.id;
  const isBrand = useCallback(
    (company: CanaryCompanyVisibilityScore) => company.id === customerId,
    [customerId],
  );
  const models = useMemo(
    () =>
      [
        ...new Set(companies.flatMap((company) => company.scores.map((score) => score.model))),
      ].sort(),
    [companies],
  );
  const companyLabel = t`Company`;
  const chatgptLabel = t`ChatGPT`;
  const claudeLabel = t`Claude`;
  const geminiLabel = t`Gemini`;
  const perplexityLabel = t`Perplexity`;
  const changeLabel = t`7d change`;
  const visibilityIndexLabel = t`Visibility index`;

  const columns = useMemo<DataTableColumnDef<CanaryCompanyVisibilityScore>[]>(() => {
    const modelNames: Record<string, string> = {
      chatgpt: chatgptLabel,
      claude: claudeLabel,
      gemini: geminiLabel,
      perplexity: perplexityLabel,
    };
    return [
      {
        id: 'company',
        accessorKey: 'company',
        header: companyLabel,
        size: 200,
        flex: 2,
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <LetteredAvatar
              name={row.original.company}
              size="large"
              className="size-7 shrink-0 text-sm"
            />
            <div className="flex flex-col">
              <span className="font-semibold">
                {row.original.company}{' '}
                {isBrand(row.original) && (
                  <span className="font-normal text-gray-500">
                    (<Trans>you</Trans>)
                  </span>
                )}
              </span>
              <span className="text-gray-500">{row.original.domain}</span>
            </div>
          </div>
        ),
      },
      ...models.map((model): DataTableColumnDef<CanaryCompanyVisibilityScore> => ({
        id: model,
        header:
          modelNames[model.toLowerCase()] ?? `${model.charAt(0).toUpperCase()}${model.slice(1)}`,
        accessorFn: (company) => company.scores.find((score) => score.model === model)?.score,
        size: 115,
        flex: 1,
        cell: ({ getValue }) => {
          const score = getValue<number | undefined>();
          return score == null ? '—' : numberFormat(score, { precision: 0 });
        },
      })),
      {
        id: 'change',
        accessorKey: 'change',
        header: changeLabel,
        size: 125,
        cell: ({ row }) =>
          row.original.change == null ? (
            '—'
          ) : (
            <DeltaLabel percentChange={row.original.change} colorize />
          ),
      },
      {
        id: 'overallScore',
        accessorKey: 'overallScore',
        header: visibilityIndexLabel,
        size: 140,
        sortFn: (rowA, rowB) => {
          const scoreA = rowA.original.overallScore;
          const scoreB = rowB.original.overallScore;
          if (scoreA == null) return scoreB == null ? 0 : -1;
          if (scoreB == null) return 1;
          return scoreA - scoreB;
        },
        cell: ({ row }) =>
          row.original.overallScore == null ? (
            '—'
          ) : (
            <span className="font-semibold">
              {numberFormat(row.original.overallScore, { precision: 0 })}
            </span>
          ),
      },
    ];
  }, [
    models,
    isBrand,
    companyLabel,
    chatgptLabel,
    claudeLabel,
    geminiLabel,
    perplexityLabel,
    changeLabel,
    visibilityIndexLabel,
  ]);

  return (
    <Panel
      title={<Trans>Competitor</Trans>}
      subtitle={<Trans>Visibility score across all tracked LLMs.</Trans>}
      isFullHeight
    >
      <DataTable<CanaryCompanyVisibilityScore>
        columns={columns}
        data={companies}
        loading={isLoading}
        error={error}
        emptyMessage={<Trans>No competitor visibility scores available.</Trans>}
        pagination={false}
        initialColumnPinning={{ start: ['company'] }}
        defaultSorting={[{ id: 'overallScore', desc: true }]}
        getRowId={(company) => company.id}
        onRow={(company) => ({
          className: isBrand(company) ? 'bg-yellow-50 [&_td]:bg-yellow-50!' : undefined,
        })}
      />
    </Panel>
  );
};

export default AIVisibilityCompetitorTable;
